import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sun, Moon, Monitor } from "lucide-react";
import styles from "./ThemeToggle.module.css";

type Theme = "light" | "dark" | "system";

const themes: Theme[] = ["system", "light", "dark"];

const icons: Record<Theme, React.ReactNode> = {
	system: <Monitor size={16} />,
	light: <Sun size={16} />,
	dark: <Moon size={16} />,
};

export function ThemeToggle() {
	const { t } = useTranslation();
	const [theme, setTheme] = useState<Theme>(() => {
		return (localStorage.getItem("theme") as Theme | null) ?? "system";
	});

	const apply = useCallback((t: Theme) => {
		const root = document.documentElement;
		if (t === "dark") {
			root.classList.add("dark");
		} else if (t === "light") {
			root.classList.remove("dark");
		} else {
			const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
			root.classList.toggle("dark", prefersDark);
		}
	}, []);

	useEffect(() => {
		apply(theme);
		localStorage.setItem("theme", theme);
	}, [theme, apply]);

	useEffect(() => {
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const handler = () => {
			if (theme === "system") apply("system");
		};
		mq.addEventListener("change", handler);
		return () => mq.removeEventListener("change", handler);
	}, [theme, apply]);

	const cycle = () => {
		const idx = themes.indexOf(theme);
		const next = themes[(idx + 1) % themes.length]!;
		setTheme(next);
	};

	const label =
		theme === "system" ? t("theme.system") : theme === "light" ? t("theme.light") : t("theme.dark");

	return (
		<button type="button" className={styles.toggle} onClick={cycle} aria-label={t("themeSwitch")}>
			{icons[theme]}
			<span>{label}</span>
		</button>
	);
}
